import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaCuatro(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11)); // landscape-ish
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // split ratio (left gray / right white)
      const leftRatio = typeof opts.leftRatio === 'number' ? opts.leftRatio : 0.30;
      const leftW = Math.round(width * leftRatio);
      const rightX = leftW;

      // left: gray
      ctx.fillStyle = opts.leftBg || '#ffffff';
      ctx.fillRect(0, 0, leftW, height);

      // right: white
      ctx.fillStyle = opts.rightBg || '#ffffff';
      ctx.fillRect(rightX, 0, width - rightX, height);

      // subtle divider
      ctx.fillStyle = opts.dividerColor || '#ffffff';
      ctx.fillRect(Math.max(0, rightX - 1), 16, 2, height - 32);

      // --- content on left (gray) side ---
      const leftPadding = typeof opts.leftPadding === 'number' ? opts.leftPadding : 32;
      let leftContentX = leftPadding;
      let leftContentY = 100;
      // draw square number (like "1.1") at the very left of the gray side and shift content right
      const squareSize = typeof opts.leftSquareSize === 'number' ? opts.leftSquareSize : 70;
      const sqX = leftPadding;
      const sqY = leftContentY;
      // square background
      ctx.save();
      ctx.fillStyle = opts.leftSquareBg || '#5fe7ffff';
      ctx.fillRect(sqX, sqY, squareSize, squareSize);
      // square number text
      ctx.fillStyle = opts.leftSquareColor || '#01090cff';
      ctx.font = `bold ${opts.leftSquareFontSize || 40}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(opts.leftSquareText || '1.1', sqX + squareSize / 2, sqY + squareSize / 2);
      ctx.restore();
      // move content start to the right of the square (plus a small gap)
      leftContentX = sqX + squareSize + (typeof opts.leftSquareGap === 'number' ? opts.leftSquareGap : 12);
      const leftMaxW = leftW - leftContentX - leftPadding;

      const wrapText = (c, text, x, y0, maxWidth, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let lineWords = [];
        let yPos = y0;
        
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxWidth && line) {
            // Justify this line (except if it's the last line)
            const isLastLine = n === words.length - 1;
            if (!isLastLine && lineWords.length > 1) {
              const lineText = lineWords.join(' ');
              const textWidth = c.measureText(lineText).width;
              const extraSpace = maxWidth - textWidth;
              const gaps = lineWords.length - 1;
              const spacePerGap = extraSpace / gaps;
              
              let currentX = x;
              for (let i = 0; i < lineWords.length; i++) {
                c.fillText(lineWords[i], currentX, yPos);
                const wordWidth = c.measureText(lineWords[i]).width;
                currentX += wordWidth + c.measureText(' ').width + spacePerGap;
              }
            } else {
              c.fillText(line, x, yPos);
            }
            
            line = words[n];
            lineWords = [words[n]];
            yPos += lh;
          } else {
            line = test;
            if (line === words[n]) {
              lineWords = [words[n]];
            } else {
              lineWords.push(words[n]);
            }
          }
        }
        // Last line is not justified
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      // Provided content (fixed)
      const title = opts.title || 'Recepción de población desplazada proveniente de otros municipios de ANTIOQUIA';
      const subTitle = opts.subTitle || '';
      const textoUno = opts.textoUno || 'Tabla 1. Principales subregiones con mayor expulsión de población desplazada proveniente de Antioquia con recepción en Medellín, semana actual.';
      const textoDos = opts.textoDos || '';

      // title — span across both left and right sides of canvas
      const rightPadding = typeof opts.rightPadding === 'number' ? opts.rightPadding : 32;
      const titleMaxW = width - leftContentX - rightPadding;
      const titleSize = (typeof opts.leftTitleSize === 'number')
        ? opts.leftTitleSize
        : 40;
      ctx.fillStyle = opts.leftTitleColor || '#0f172a';
      ctx.font = `bold ${titleSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      leftContentY = wrapText(ctx, title, leftContentX, leftContentY, titleMaxW, Math.round(titleSize * 1.4));
      
      // save position after title for right content
      const afterTitleY = leftContentY;

      // subtitle (smaller, muted)
      ctx.fillStyle = opts.leftSubTitleColor || '#475569';
      ctx.font = `${opts.leftSubTitleSize || 40}px Inter, Arial, sans-serif`;
      leftContentY = wrapText(ctx, subTitle, leftContentX, leftContentY + 8, leftMaxW, Math.round((opts.leftSubTitleSize || 40) * 1.4));

      // spacer before paragraphs
      leftContentY += 12;

      // paragraph textoUno
      ctx.fillStyle = opts.leftTextColor || '#334155';
      ctx.font = `${opts.leftTextSize || 25}px Inter, Arial, sans-serif`;
      leftContentY = wrapText(ctx, textoUno, leftContentX, leftContentY + 100, leftMaxW, Math.round((opts.leftTextSize || 25) * 1.6));

      // small note textoDos (muted)
      ctx.fillStyle = opts.leftNoteColor || '#475569';
      ctx.font = `${opts.leftNoteSize || 25}px Inter, Arial, sans-serif`;
      leftContentY = wrapText(ctx, textoDos, leftContentX, leftContentY + 100, leftMaxW, Math.round((opts.leftNoteSize || 25) * 1.6));

      const mainLogoSrc = opts.logoSrc || logoSrc;
      const logoPromises = [];

      // existing bottom-right logo (if provided) — load later together with left logo
      const mainLogoPromise = mainLogoSrc
        ? new Promise((res) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try {
                const maxW = Math.min(200, Math.round(width * 0.18));
                const w = Math.min(maxW, img.width);
                const h = (img.height / img.width) * w;
                const lx = width - 16 - w;
                const ly = height - 16 - h;
                ctx.drawImage(img, lx, ly, w, h);
              } catch (e) { /* ignore */ }
              res();
            };
            img.onerror = () => res();
            img.src = mainLogoSrc;
          })
        : Promise.resolve();
      logoPromises.push(mainLogoPromise);

      // --- table: four columns on the RIGHT (Subregiones | Municipio | Total hogares | Total personas) ---
      const tableData = opts.tableData || props.tableData || [];
      // right side content area — start after title
      const rightContentX = rightX + rightPadding;
      const rightContentY = afterTitleY + 20; // start after title with some spacing
      const rightMaxW = (width - rightX) - rightPadding * 2;

      // allocate a narrow summary column on the far right and assign remaining width to the table
      const summaryColW = typeof opts.summaryColumnWidth === 'number' ? opts.summaryColumnWidth : 220;
      const summaryColGap = typeof opts.summaryColumnGap === 'number' ? opts.summaryColumnGap : 12;
      const tableAreaW = Math.max(200, rightMaxW - summaryColW - summaryColGap);
      const tableX = rightContentX;
      const cardsX = tableX + tableAreaW + summaryColGap;
      const cardsColumnWidth = summaryColW;
      // table start Y
      const tableY = rightContentY + 12;

      // table layout on right (use tableAreaW)
      const rowH = Math.round((opts.tableRowHeight || 28));
      const headerH = Math.round(rowH * 1.1);
      // columns based on tableAreaW: 40% | 30% | 15% | 15%
      const col1R = Math.floor(tableAreaW * 0.40); // Subregiones
      const col2R = Math.floor(tableAreaW * 0.30); // Municipio
      const col3R = Math.floor(tableAreaW * 0.15); // Total hogares
      const col4R = tableAreaW - col1R - col2R - col3R; // Total personas

      // header (on white side) — center total column headers
      ctx.save();
      ctx.fillStyle = opts.tableHeaderBg || '#0f172a';
      ctx.fillRect(tableX, tableY, tableAreaW, headerH);
      ctx.fillStyle = opts.tableHeaderColor || '#ffffff';
      ctx.font = `${opts.tableHeaderSize || 20}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const hh = tableY + Math.round(headerH / 2);
      ctx.fillText('Subregiones', tableX + 8, hh);
      ctx.fillText('Municipio', tableX + col1R + 8, hh);
      // center headers for total columns
      ctx.textAlign = 'center';
      const hogaresColCenterX = tableX + col1R + col2R + Math.round(col3R / 2);
      const personasColCenterX = tableX + col1R + col2R + col3R + Math.round(col4R / 2);
      ctx.fillText('Hogares', hogaresColCenterX, hh);
      ctx.fillText('Personas', personasColCenterX, hh);
      ctx.restore();

      // rows (on right) — group by subregion, show each municipio on its own row and place its own values in columns 3 and 4
      const groupMap = {};
      for (let i = 0; i < tableData.length; i++) {
        const r = tableData[i] || {};
        const sub = String(r.subregion || '').trim();
        const muni = String(r.municipio || '').trim();
        const totalHog = parseInt(String(r.totalHogares || '0').replace(/\D/g, ''), 10) || 0;
        const totalPers = parseInt(String(r.totalPersonas || '0').replace(/\D/g, ''), 10) || 0;
        if (!groupMap[sub]) groupMap[sub] = { subregion: sub, municipios: {} };
        if (!groupMap[sub].municipios[muni]) groupMap[sub].municipios[muni] = { hogares: 0, personas: 0 };
        groupMap[sub].municipios[muni].hogares += totalHog;
        groupMap[sub].municipios[muni].personas += totalPers;
      }
      // convert and sort
      const grouped = Object.keys(groupMap)
        .map(k => ({
          subregion: groupMap[k].subregion,
          municipios: Object.keys(groupMap[k].municipios)
            .map(m => ({ 
              municipio: m, 
              totalHogares: groupMap[k].municipios[m].hogares,
              totalPersonas: groupMap[k].municipios[m].personas
            }))
            .sort((a, b) => b.totalHogares - a.totalHogares)
        }))
        .map(g => ({
          ...g,
          totalPersonas: g.municipios.reduce((sum, m) => sum + m.totalPersonas, 0),
          totalHogares: g.municipios.reduce((sum, m) => sum + m.totalHogares, 0)
        }))
        .sort((a, b) => b.totalHogares - a.totalHogares);

        
      // show first 5 subregions, keep the rest for text display below table
      const displayGroups = grouped.slice(0, 5);
      const remainingGroups = grouped.slice(5);
      
      // calculate totals for remaining groups (to display as text below table)
      let otrasSubregionesText = '';
      if (remainingGroups.length > 0) {
        const totalHogaresOtros = remainingGroups.reduce((sum, g) => 
          sum + g.municipios.reduce((s, m) => s + m.totalHogares, 0), 0
        );
        const totalPersonasOtros = remainingGroups.reduce((sum, g) => 
          sum + g.municipios.reduce((s, m) => s + m.totalPersonas, 0), 0
        );
        const subregionNames = remainingGroups.map(g => g.subregion).join(', ');
        otrasSubregionesText = `*Otras subregiones: en la semana actual se recibieron, en menor intensidad, hogares del ${subregionNames} (Total: ${totalHogaresOtros} hogares, ${totalPersonasOtros} personas)`;
        
        // display "Otras subregiones" text on left side below textoDos
        ctx.fillStyle = opts.leftNoteColor || '#475569';
        ctx.font = `${opts.leftNoteSize || 20}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        leftContentY = wrapText(ctx, otrasSubregionesText, leftContentX, leftContentY + 12, leftMaxW, Math.round((opts.leftNoteSize || 25) * 1.6));
      }

      // draw rows: subregion once, then one row per municipio with its own value in column 3
      let ry = tableY + headerH;
      const baseRowH = rowH;
      // table body text normal by default
      const rowFont = `${opts.tableRowSize || 16}px Inter, Arial, sans-serif`;
      const rowFontBold = `bold ${opts.tableRowSize || 16}px Inter, Arial, sans-serif`;
      ctx.font = rowFont;
       for (let gi = 0; gi < displayGroups.length; gi++) {
         const row = displayGroups[gi];
         const municipios = row.municipios || [];
         const lines = Math.max(1, municipios.length);
         const totalRowCount = lines + 1; // extra row for subregion total
         const totalRowGap = 10;
         const blockH = baseRowH * totalRowCount + totalRowGap;

        // background for the whole block (cells backcolor = white)
        ctx.fillStyle = opts.tableRowBg || '#ffffff';
        ctx.fillRect(tableX, ry, tableAreaW, blockH);

        // subregion: draw once, aligned to top of the block
        ctx.fillStyle = opts.tableRowColor || '#0f172a';
        ctx.textBaseline = 'top';
        ctx.font = rowFont; // ensure normal weight for labels and municipios
        const subTopY = ry + Math.round(baseRowH * 0.2);
        ctx.fillText(String(row.subregion || ''), tableX + 8, subTopY);

         // municipalities: each on its own row in column 2, show its values in columns 3 and 4 on the same row
         const muniX = tableX + col1R + 8;
         let subtotalHogares = 0;
         let subtotalPersonas = 0;
         for (let mi = 0; mi < municipios.length; mi++) {
            const m = municipios[mi];
            const muniText = m.municipio || '';
            const mTotalHog = typeof m.totalHogares === 'number' ? m.totalHogares : parseInt(String(m.totalHogares || '0').replace(/\D/g, ''), 10) || 0;
            const mTotalPers = typeof m.totalPersonas === 'number' ? m.totalPersonas : parseInt(String(m.totalPersonas || '0').replace(/\D/g, ''), 10) || 0;
            subtotalHogares += mTotalHog;
            subtotalPersonas += mTotalPers;
 
            // truncate per-line to fit col2R
            let muniDisplay = muniText;
            while (ctx.measureText(muniDisplay).width > col2R - 16 && muniDisplay.length > 0) {
              muniDisplay = muniDisplay.slice(0, -1);
            }
            if (muniDisplay.length < muniText.length) muniDisplay = muniDisplay.slice(0, Math.max(0, muniDisplay.length - 3)) + '...';
 
            const lineMidY = ry + mi * baseRowH + Math.round(baseRowH / 2);
            // municipality name (normal)
            ctx.font = rowFont;
            ctx.textAlign = 'left';
            ctx.fillText(muniDisplay, muniX, lineMidY);
 
           // municipio total hogares (NORMAL) - CENTER in col3
           ctx.font = rowFont;
           ctx.textAlign = 'center';
           ctx.fillText(String(mTotalHog), hogaresColCenterX, lineMidY);
           // municipio total personas (NORMAL) - CENTER in col4
           ctx.fillText(String(mTotalPers), personasColCenterX, lineMidY);
           // restore normal align/font for next line
           ctx.textAlign = 'left';
           ctx.font = rowFont;
         }
 
         // draw subtotal row (below the last municipio row) inside table area
         const totalRowY = ry + lines * baseRowH + totalRowGap;
         // total row background also white (cell backcolor = white)
         ctx.fillStyle = opts.tableTotalRowBg || '#ffffff';
         ctx.fillRect(tableX, totalRowY, tableAreaW, baseRowH);
         // label "Total" in second column, left-aligned (force non-bold)
        ctx.fillStyle = opts.tableTotalLabelColor || '#0f172a';
        const totalLabelFontSize = opts.tableTotalLabelSize || (opts.tableRowSize || 16);
        ctx.font = `bold ${totalLabelFontSize}px Inter, Arial, sans-serif`;
         ctx.textBaseline = 'middle';
         const totalLabelX = muniX;
         const totalMidY = totalRowY + Math.round(baseRowH / 2);
         ctx.textAlign = 'left';
         ctx.fillText('Subtotal', totalLabelX, totalMidY);
         // subtotal values bold and CENTER in columns 3 and 4
        ctx.font = rowFontBold;
        ctx.textAlign = 'center';
        ctx.fillText(String(subtotalHogares), hogaresColCenterX, totalMidY);
        ctx.fillText(String(subtotalPersonas), personasColCenterX, totalMidY);
         ctx.textAlign = 'left';
         ctx.font = rowFont;
 
         // bottom border for the block (table area only) — thicker 2px
         ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
         ctx.lineWidth = 2;
         ctx.beginPath();
         ctx.moveTo(tableX, ry + blockH - 1);
         ctx.lineTo(tableX + tableAreaW, ry + blockH - 1);
         ctx.stroke();

         ry += blockH;
       }

      // outer table border (2px) around the full table area
      try {
        const tableHeight = Math.max(1, ry - tableY);
        ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 2;
        // outer border
        ctx.strokeRect(tableX + 1, tableY + 1, tableAreaW - 2, tableHeight - 2);

        // vertical column separators (2px) between columns
        const sep1 = Math.round(tableX + col1R);
        const sep2 = Math.round(tableX + col1R + col2R);
        const sep3 = Math.round(tableX + col1R + col2R + col3R);
        ctx.beginPath();
        ctx.moveTo(sep1 + 0.5, tableY);
        ctx.lineTo(sep1 + 0.5, tableY + tableHeight);
        ctx.moveTo(sep2 + 0.5, tableY);
        ctx.lineTo(sep2 + 0.5, tableY + tableHeight);
        ctx.moveTo(sep3 + 0.5, tableY);
        ctx.lineTo(sep3 + 0.5, tableY + tableHeight);
        ctx.stroke();
      } catch (e) { /* ignore */ }

      // --- summary cards as a vertical column to the RIGHT of the table ---
      const resumenTabla = opts.resumenTabla || props.resumenTabla || [
        { label: 'Total Antioquia (sin Medellín)', value: '0' },
        { label: 'Porcentaje en Antioquia (sin Medellín)', value: '0%' },
      ];
      const cardsGap = typeof opts.summaryCardsGap === 'number' ? opts.summaryCardsGap : 12;
      const cardHeight = typeof opts.summaryCardHeight === 'number' ? opts.summaryCardHeight : 200;
      const cardsStartY = tableY; // align top of cards with table header

      const drawSummaryCard = (idx, x, y, w, h, item) => {
        // card box (default: yellow)
        ctx.fillStyle = opts.summaryCardBg || '#e7d488ff';
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = opts.summaryCardBorder || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 1;
        ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

        const centerX = x + Math.round(w / 2);
        const pad = 12;

        // helper: wrap text into lines that fit maxWidth
        const wrapLines = (c, text, maxWidth) => {
          const words = String(text).split(' ').filter(Boolean);
          const lines = [];
          let line = '';
          for (let i = 0; i < words.length; i++) {
            const test = line ? line + ' ' + words[i] : words[i];
            if (c.measureText(test).width > maxWidth && line) {
              lines.push(line);
              line = words[i];
            } else {
              line = test;
            }
          }
          if (line) lines.push(line);
          return lines;
        };

        // draw label: wrap and center the block inside the top area of the card
        ctx.fillStyle = opts.summaryLabelColor || '#334155';
        const labelFontSize = opts.summaryLabelSize || 23;
        ctx.font = `${labelFontSize}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const maxLabelW = w - pad * 2;
        const labelLines = wrapLines(ctx, String(item.label || ''), maxLabelW);
        const lineH = Math.round(labelFontSize * 1.2);
        // target center for label block (around 33% of card height)
        const labelBlockCenterY = y + Math.round(h * 0.32);
        const labelBlockH = Math.max(1, labelLines.length) * lineH;
        let labelStartY = labelBlockCenterY - Math.round(labelBlockH / 2) + Math.round(lineH / 2);
        labelLines.forEach((ln, i) => {
          ctx.fillText(ln, centerX, labelStartY + i * lineH);
        });

        // draw value: single line, centered horizontally and placed lower in card
        ctx.fillStyle = opts.summaryValueColor || '#0f172a';
        const valueFontSize = opts.summaryValueSize || 30;
        ctx.font = `bold ${valueFontSize}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const valueY = y + Math.round(h * 0.68);
        ctx.fillText(String(item.value || ''), centerX, valueY);
      };

      // draw column of cards
      for (let ci = 0; ci < Math.min(resumenTabla.length, 3); ci++) {
        const item = resumenTabla[ci];
        const y = cardsStartY + ci * (cardHeight + cardsGap);
        drawSummaryCard(ci, cardsX, y, cardsColumnWidth, cardHeight, item);
      }

      // wait for logos to load then resolve draw()
      Promise.all(logoPromises).then(() => resolve());
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_cuatro_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});